import { useContext, useMemo, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { SPAViewContext } from '@confluence/spa-view-context';
import { useIsProductAdmin } from '@confluence/current-user';

import { SaveCompanyHubConfirmationModalLoadable } from './SaveCompanyHubConfirmationModalLoadable';
import { getCompanyHubAnnouncementBanner } from './getCompanyHubAnnouncementBanner';

export const useSaveCompanyHub = () => {
	const { tenantId } = useContext(SPAViewContext);
	const { isProductAdmin } = useIsProductAdmin();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireRequestEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'requested',
				actionSubject: 'saveHubConfirmationDialog',
				source: 'companyHub',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const getSuccessModalDialogData = useCallback(
		async (contentId) => {
			if (!isProductAdmin) {
				fireRequestEvent();
				return {
					isVisible: false,
					modalDialog: SaveCompanyHubConfirmationModalLoadable,
				};
			}

			const { bannerId, bannerStatus } = await getCompanyHubAnnouncementBanner(contentId, tenantId);

			if (!bannerId) {
				fireRequestEvent();
				return {
					isVisible: true,
					modalDialog: SaveCompanyHubConfirmationModalLoadable,
				};
			}

			const isBannerActive = !!bannerStatus && bannerStatus !== 'SAVED'; // 'SAVED' means archived

			if (isBannerActive) {
				fireRequestEvent();
			}
			return {
				isVisible: isBannerActive,
				bannerId,
				modalDialog: SaveCompanyHubConfirmationModalLoadable,
			};
		},
		[isProductAdmin, tenantId, fireRequestEvent],
	);

	return useMemo(
		() => ({
			getSuccessModalDialogData,
		}),
		[getSuccessModalDialogData],
	);
};
